import type { Attachment } from '../types';
import { api } from '../api';

interface AttachmentRendererProps {
  attachment: Attachment;
}

const MAX_INLINE_IMAGE = 10 * 1024 * 1024; // images larger than this render as a file link

export function AttachmentRenderer({ attachment }: AttachmentRendererProps) {
  const url = api.attachmentUrl(attachment.id);

  if (attachment.contentType.startsWith('image/') && attachment.byteSize <= MAX_INLINE_IMAGE) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block">
        <img
          src={url}
          alt={attachment.filename}
          loading="lazy"
          className="max-h-80 max-w-full rounded-lg border border-zinc-800 object-contain bg-zinc-950"
        />
      </a>
    );
  }

  if (attachment.contentType.startsWith('video/')) {
    return (
      <video
        controls
        preload="metadata"
        className="max-h-80 max-w-full rounded-lg border border-zinc-800 bg-zinc-950"
      >
        <source src={url} type={attachment.contentType} />
      </video>
    );
  }

  if (attachment.contentType.startsWith('audio/')) {
    return (
      <audio controls preload="metadata" className="w-full max-w-sm">
        <source src={url} type={attachment.contentType} />
      </audio>
    );
  }

  // Generic file
  return (
    <a
      href={api.attachmentUrl(attachment.id, true)}
      className="inline-flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 hover:border-zinc-700 transition no-underline"
    >
      <div className="w-10 h-10 rounded bg-zinc-800 flex items-center justify-center text-zinc-400 text-xs font-mono shrink-0">
        {fileExt(attachment.filename) || '?'}
      </div>
      <div className="min-w-0">
        <p className="text-sm text-zinc-100 truncate max-w-[16rem]">{attachment.filename}</p>
        <p className="text-[10px] text-zinc-500">{formatBytes(attachment.byteSize)}</p>
      </div>
    </a>
  );
}

function fileExt(name: string): string {
  const i = name.lastIndexOf('.');
  if (i < 0) return '';
  return name.slice(i + 1).toUpperCase().slice(0, 4);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}
